/**
 * Places of Relevance Section Component
 * Displays horizontal scrollable activity cards
 * Uses reusable CollapsibleSection and ScrollableCardList components
 */

import React, { useMemo } from 'react';
import {
  spacing,
  typography,
  typographyColors,
  fontPrimary,
} from '@/design-system/utils';
import { ActivityCard as ActivityCardType } from '@/constants/chatScreenData';
import { ChatLocation } from '@/types/chatTypes';
import { PlaceGridCard } from './PlaceGridCard';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { ScrollableCardList } from '@/components/ui/scrollable-card-list';

export interface PlacesOfRelevanceSectionProps {
  title: string;
  locations: ChatLocation[]; // All valid locations (tourist attractions and businesses)
  onCardClick?: (cardId: string) => void;
  onArrowClick?: () => void;
  /**
   * Whether the section should be collapsed by default
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * Controlled collapsed state. If provided, component becomes controlled.
   */
  isCollapsed?: boolean;
  /**
   * Callback when collapse button is clicked (for controlled mode)
   */
  onToggle?: (collapsed: boolean) => void;
}

export const PlacesOfRelevanceSection: React.FC<
  PlacesOfRelevanceSectionProps
> = ({
  title,
  locations,
  onCardClick,
  onArrowClick,
  defaultCollapsed = false,
  isCollapsed,
  onToggle,
}) => {
  // Map all locations to cards
  const cards = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Map to ActivityCard format (includes both tourist attractions and businesses)
    return locations.map((location, index): ActivityCardType => {
      // Handle both image_urls (snake_case) and imageUrls (camelCase) from API
      const imageUrls =
        location.image_urls ||
        (location as unknown as { imageUrls?: string[] }).imageUrls ||
        [];

      // Filter out invalid URLs (empty strings, null, undefined)
      const validImageUrls = imageUrls.filter(
        url => url && typeof url === 'string' && url.trim().length > 0
      );

      // Use index to pick image, but validate that we have enough images
      // If index is out of bounds or no valid images, use the last one
      let selectedImageUrl: string | undefined;
      if (validImageUrls.length > 0) {
        if (index < validImageUrls.length) {
          // Index is valid, use image at that index
          selectedImageUrl = validImageUrls[index];
        } else {
          // Index is out of bounds, use last image in array
          selectedImageUrl = validImageUrls[validImageUrls.length - 1];
        }
      }

      return {
        id: location.place_id
          ? `${location.place_id}_${index}`
          : `location_${index}`,
        title:
          location.title ||
          location.place ||
          location.place_name ||
          'Unknown Location',
        image: selectedImageUrl || '', // Empty placeholder when no images available
        location:
          location.subtitle ||
          location.address ||
          location.locality ||
          'Location not specified',
        rating:
          typeof location.rating === 'number' ? location.rating : undefined,
      };
    });
  }, [locations]);

  if (!cards || cards.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
        }}
      >
        <h3
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            lineHeight: 'normal',
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.regular,
            color: typographyColors.primary,
            lineHeight: 'normal',
          }}
        >
          No places available at the moment.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
      }}
    >
      <CollapsibleSection
        title={title}
        defaultCollapsed={defaultCollapsed}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      >
        <ScrollableCardList onScroll={onArrowClick}>
          {cards.map((card, index) => (
            <PlaceGridCard
              key={`${card.id}_${index}`}
              card={card}
              onClick={onCardClick}
              fixedWidth={true}
            />
          ))}
        </ScrollableCardList>
      </CollapsibleSection>
    </div>
  );
};
